import {
	IconClockPlay,
	IconEdit,
	IconPlayerPlay,
	IconTrash,
} from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Grid, Input, Typography } from "antd";
import { useCallback, useMemo } from "react";
import { useProjects } from "@/common/artemis-operations/projects";
import { confirmDeleteResource } from "@/common/confirm";
import type { TaskTemplate } from "@/common/model";
import { CustomIcon, DateField, IdField } from "../base";
import {
	ResponsiveTable,
	type ResponsiveTableProps,
} from "../responsive-table";

interface TaskTemplateTableProps
	extends Omit<ResponsiveTableProps<TaskTemplate>, "columns"> {
	workspaceId: string;
	/** Opens the task create wizard pre-filled from the template. */
	onCreateTask?: (template: TaskTemplate) => void;
	/** Opens the job create wizard pre-filled from the template. */
	onScheduleJob?: (template: TaskTemplate) => void;
	onEditTemplate?: (template: TaskTemplate) => void;
	onDeleteTemplates?: (templates: TaskTemplate[]) => void;
	// Controlled server-side search (owned by the route via usePagedList).
	search?: string;
	onSearchChange?: (v: string) => void;
}

export function TaskTemplateTable({
	workspaceId,
	onCreateTask,
	onScheduleJob,
	onEditTemplate,
	onDeleteTemplates,
	loading,
	search,
	onSearchChange,
	...props
}: TaskTemplateTableProps) {
	const screens = Grid.useBreakpoint();
	const isCompact = !screens.lg;
	const { data: projects } = useProjects(workspaceId);

	// Templates store a projectId only; resolve the display name from the already-cached
	// workspace project list rather than adding a nested selection to the query.
	const projectName = useCallback(
		(projectId?: string | null) =>
			projects?.find((p) => p?.id === projectId)?.name ?? "-",
		[projects],
	);

	const columns: ColumnDef<TaskTemplate>[] = useMemo(
		() => [
			{
				accessorKey: "name",
				header: "Name",
				cell: ({ row }) => (
					<Typography.Text ellipsis strong>
						{row.original.name}
					</Typography.Text>
				),
			},
			{
				accessorKey: "projectId",
				header: "Project",
				size: 180,
				enableSorting: true,
				cell: ({ row }) => (
					<Typography.Text ellipsis>
						{row.original.projectId
							? projectName(row.original.projectId)
							: `${row.original.environmentIds.length} environment(s)`}
					</Typography.Text>
				),
			},
			{
				accessorKey: "id",
				header: "ID",
				size: 200,
				enableSorting: true,
				enableHiding: true,
				cell: ({ row }) => (
					<div style={{ display: "flex", alignItems: "center" }}>
						<IdField id={row.original.id as string} />
					</div>
				),
			},
			{
				accessorKey: "updatedAt",
				header: "Updated",
				size: 120,
				enableSorting: true,
				cell: ({ row }) => (
					<DateField value={row.original.updatedAt as string} />
				),
			},
		],
		[projectName],
	);

	const confirmDelete = (templates: TaskTemplate[]) =>
		confirmDeleteResource(() => onDeleteTemplates?.(templates));

	return (
		<ResponsiveTable<TaskTemplate>
			loading={loading}
			columns={columns}
			initialSorting={[{ id: "updatedAt", desc: true }]}
			rowActions={[
				{
					label: "Create task",
					icon: <CustomIcon icon={IconPlayerPlay} />,
					onClick: (template) => onCreateTask?.(template),
				},
				{
					label: "Schedule job",
					icon: <CustomIcon icon={IconClockPlay} />,
					onClick: (template) => onScheduleJob?.(template),
				},
				{
					label: "Edit",
					icon: <CustomIcon icon={IconEdit} />,
					onClick: (template) => onEditTemplate?.(template),
				},
				{
					label: "Delete",
					icon: <CustomIcon icon={IconTrash} />,
					danger: true,
					onClick: (template) => confirmDelete([template]),
				},
			]}
			compactRender={(template) => (
				<article
					onKeyDown={() => onCreateTask?.(template)}
					onClick={() => onCreateTask?.(template)}
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
						gap: 8,
					}}
				>
					<Typography.Text strong ellipsis>
						{template.name}
					</Typography.Text>
					<Typography.Text type="secondary" ellipsis>
						{template.projectId ? projectName(template.projectId) : ""}
					</Typography.Text>
				</article>
			)}
			filterBar={() => (
				<Input.Search
					placeholder="Search by name or ID"
					allowClear
					style={{ width: isCompact ? "100%" : 220 }}
					value={search ?? ""}
					onChange={(e) => onSearchChange?.(e.target.value)}
					onSearch={(v) => onSearchChange?.(v)}
				/>
			)}
			onRowClick={(template) => onCreateTask?.(template)}
			selectionAction={
				onDeleteTemplates
					? {
							label: "Delete",
							icon: <CustomIcon icon={IconTrash} />,
							variant: "destructive",
							onClick: confirmDelete,
							disabled: loading,
						}
					: undefined
			}
			{...props}
		/>
	);
}
